import { Button, Modal, TextBlock, Title } from '@skillarea/lib-frontend';
import type { BlockedAccountCookieDto } from '@skillarea/lib-shared';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import styles from './AccountBlockedModal.module.scss';

import { useAccountBlockedStore } from '~/stores/accountBlockedStore';

function formatBanUntil(
  value: Date | null | undefined,
  locale: string,
): string | undefined {
  if (!value || Number.isNaN(value.getTime())) {
    return;
  }

  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(value);
}

function getBlockedMessage(
  t: TFunction<'modals'>,
  banStatus: BlockedAccountCookieDto,
  locale: string,
): string {
  if (banStatus.isPermanent) {
    return t('accountBlocked.permanent');
  }

  const formattedDate = formatBanUntil(banStatus.banUntil, locale);
  if (!formattedDate) {
    return t('accountBlocked.temporary');
  }

  return t('accountBlocked.temporaryUntil', { date: formattedDate });
}

export function AccountBlockedModal() {
  const { i18n, t } = useTranslation('modals');
  const banStatus = useAccountBlockedStore((state) => state.banStatus);
  const dismiss = useAccountBlockedStore((state) => state.dismiss);
  const isOpen = useAccountBlockedStore((state) => state.isOpen);
  const reason = banStatus?.reason?.trim();

  if (!banStatus) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onBeforeClose={dismiss}
      title={t('accountBlocked.title')}
    >
      <div className={styles.content} data-testid="account-banned-modal">
        <Title as="h2" color="accent" size="xs">
          {t('accountBlocked.heading')}
        </Title>
        <TextBlock color="contrast" data-testid="account-banned-message">
          {getBlockedMessage(t, banStatus, i18n.language)}
        </TextBlock>
        {reason ? (
          <TextBlock
            className={styles.reason}
            color="secondary"
            data-testid="account-banned-reason"
          >
            {t('accountBlocked.reason', { reason })}
          </TextBlock>
        ) : null}
        <div className={styles.actions}>
          <Button color="accent" onClick={dismiss} size="s" variant="outline">
            {t('common.close')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
